import { useEffect, useMemo, useRef } from "react";
import { CloseCircleOutlined, WarningOutlined } from "@ant-design/icons";
import { setAiHighlightedDataObjects } from "store/dataObjects/actions";
import { filterErroredDataObjects } from "./match";
import type { ToolPartProps } from "./types";

interface FindErroredDataObjectsInput {
    // Optional: narrow to records that failed validation on this field (by name).
    field?: string;
    description: string;
}

interface ErroredRecordReason {
    field: string;
    reasons: string[];
}

interface FindErroredDataObjectsOutput {
    matchCount: number;
    dataObjectIds: string[];
    records: Array<{ dataObjectId: string; errors: ErroredRecordReason[] }>;
    description: string;
}

export default function FindErroredDataObjectsTool({ toolCallId, state, input, output, errorText, helpers }: ToolPartProps<FindErroredDataObjectsInput, FindErroredDataObjectsOutput>) {
    const { addToolOutput, dispatch, dataObjects, schemaFields } = helpers;
    const resolvedRef = useRef(false);

    const matches = useMemo(() => {
        return filterErroredDataObjects(dataObjects, schemaFields, input?.field);
    }, [input, dataObjects, schemaFields]);

    useEffect(() => {
        if (resolvedRef.current) return;
        if (state !== "input-available") return;
        resolvedRef.current = true;
        const ids = matches.map((m) => m.dataObjectId);
        dispatch(setAiHighlightedDataObjects(ids));
        addToolOutput({
            tool: "findErroredDataObjects",
            toolCallId,
            output: {
                matchCount: ids.length,
                dataObjectIds: ids,
                records: matches.slice(0, 25).map((m) => ({ dataObjectId: m.dataObjectId, errors: m.errors })),
                description: input?.description || "",
            },
        });
    }, [state, matches, dispatch, addToolOutput, toolCallId, input]);

    const ids = state === "output-available" && output ? output.dataObjectIds : matches.map((m) => m.dataObjectId);
    const matchCount = state === "output-available" && output ? output.matchCount : matches.length;
    const description = (state === "output-available" && output?.description) || input?.description || "";
    const sample = matches.slice(0, 5);

    return (
        <div className="rounded-2xl border border-zinc-200 bg-white p-3 text-sm text-zinc-800 shadow-sm">
            <div className="mb-1 flex items-center gap-2 text-[#b45309]">
                <WarningOutlined />
                <span className="text-xs font-semibold uppercase tracking-wider">Errored records</span>
            </div>
            {description && <div className="text-xs text-zinc-600">{description}</div>}
            <div className="mt-1 text-xs text-zinc-500">
                {matchCount === 0 ? "No records failed validation." : `${matchCount} errored record${matchCount === 1 ? "" : "s"} highlighted in the list.`}
            </div>
            {sample.length > 0 && (
                <ul className="mt-2 flex flex-col gap-1">
                    {sample.map((m) => (
                        <li key={m.dataObjectId} className="rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] text-amber-900">
                            <span className="font-mono text-[10px] text-amber-700">{m.dataObjectId.slice(0, 8)}</span>
                            {": "}
                            {m.errors.map((e) => `${e.field} (${e.reasons.join("; ")})`).join(", ")}
                        </li>
                    ))}
                    {matchCount > sample.length && <li className="text-[10px] text-zinc-400">...and {matchCount - sample.length} more.</li>}
                </ul>
            )}
            {state === "output-error" && (
                <div className="mt-2 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">
                    <CloseCircleOutlined /> {errorText || "Tool error."}
                </div>
            )}
        </div>
    );
}
